<template>
  <CodSheet
    v-model="open"
    :guard="!canDismiss"
    :title="config?.title"
    show-header
    :footer-chrome="false"
    width="400px"
    height="660px"
  >
    <div class="flex min-h-0 flex-1 cursor-pointer flex-col" @click="selectInputField()">
      <div
        v-if="!config?.amountDisabled"
        class="flex min-h-25 flex-1 items-center justify-center text-center"
        :class="{ 'opacity-50': selectedInputIndex !== undefined }"
      >
        <div>
          <div class="text-display-lg">
            {{ amount }}<span v-if="config?.suffix" class="text-display">{{ config.suffix }}</span>
          </div>
          <div
            v-if="config?.amountLabel"
            v-html="config?.amountLabel"
            class="text-identifier mt-2 leading-normal"
          ></div>
        </div>
      </div>

      <!-- Context lines under the amount (LabeledValue; default = body label
           + 14/600 value — they had inherited the 24px numeric-display step,
           R57). A line declares `emphasis: 'prominent'` when the user must
           read it at a glance (the cash change). Lines with nothing to show
           yet (card/cash's tip) still hold their height; on a short screen
           they are the first to give it up (the padding sits inside, so all
           of it can go). -->
      <div
        v-if="lines.length > 0"
        :class="{ 'min-h-0 overflow-hidden': labelsBlank }"
      >
        <div class="px-4 py-2">
          <LabeledValue
            v-for="(line, index) in lines"
            :key="'l' + index"
            :label="line.text[0]"
            :value="line.text[1]"
            :emphasis="line.emphasis"
            :class="line.emphasis === 'prominent' ? 'px-4 py-3' : 'h-8.75 px-4 py-2'"
          />
        </div>
      </div>

      <!-- Beside the amount the inputs keep their full height (overflow-hidden
           alone lets a flex item shrink to nothing): short of room, the body
           scrolls rather than cut a field. Without the amount (mixed payment:
           one row per payment type, often more than fit above the keypad) the
           box takes the room that is left and scrolls its own rows. -->
      <div
        v-if="inputs?.length && inputs.length > 0"
        class="info-box mx-4 mb-2"
        :class="config?.amountDisabled ? 'overflow-y-auto' : 'shrink-0 overflow-hidden'"
      >
        <div
          v-for="(input, index) in inputs"
          :key="'i' + index"
          class="flex min-h-15 cursor-pointer items-center border-l-2 px-4"
          :class="[
            index !== inputs.length - 1 ? 'border-b border-b-default' : '',
            selectedInputIndex === index && input.type === 'number' ? 'border-l-inverted' : 'border-l-transparent'
          ]"
          @click="selectInputField(index, $event)"
        >
          <template v-if="input.type === 'number'">
            <span class="flex-1">{{ input.label }}</span>
            <h1
              class="px-1.5 py-0.5 text-right text-display"
              :class="selectedInputIndex === index
                ? (highlightedInputIndex === index ? 'bg-accented' : '')
                : 'opacity-30'"
            >
              {{ input.strValue }}
            </h1>
          </template>

          <UCheckbox
            v-if="input.type === 'checkbox'"
            class="w-full flex-row-reverse justify-between"
            :model-value="(input.value as boolean)"
            :label="input.label"
            @click.stop
            @update:model-value="(v: boolean | 'indeterminate') => input.value = v === true"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <numpad
        v-model="numpadAmount"
        v-model:reset-pending="resetPending"
        :precision="config?.precision"
        :sign-button-disabled="config?.signButtonDisabled"
        :sign-button-replace="config?.signButtonReplace"
        :ok-button-enabled="okButtonEnabled"
        :max="max"
        @confirm="confirm"
      />
    </template>
  </CodSheet>
</template>

<script setup lang="ts">
/**
 * NumpadModal — amount-entry dialog on CodSheet (HIGH-RISK A2).
 * The money behavior (mutators, max/maxSum clamps, input focus semantics,
 * confirm/cancel handlers) is pinned by the characterization suite
 * (useNumpad.spec.ts). `INumpadConfig` API preserved.
 */
import { PropType, Ref, computed, ref, watch } from 'vue';
import { Money } from 'app-core';
import { INumpadConfig, INumpadInput } from '@/types/components';
import Numpad from '@/components/Numpad.vue';
import LabeledValue from '@/components/LabeledValue.vue';
import { CodSheet } from 'app-components';

type ExtendedNumpadInputType = INumpadInput & { strValue: string }

const emit = defineEmits(['update:modelValue'])

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: false
  },
  config: {
    type: Object as PropType<INumpadConfig | undefined>,
    default: () => undefined
  }
})

const open = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v)
})

const amount = ref('0')
const numpadAmount = ref('0')
const resetPending = ref(false)
const resolved = ref(true)
const inputs: Ref<(ExtendedNumpadInputType)[] | undefined> = ref([])
const selectedInputIndex: Ref<number | undefined> = ref()
const highlightedInputIndex: Ref<number | undefined> = ref()

const inputValues = computed(() => {
  return inputs.value?.map(input => input.value) || []
})

const lines = computed(() => (props.config?.labels ?? []).map(label => ({
  text: label.content(+amount.value, inputValues.value),
  emphasis: label.emphasis ?? 'default',
})))

const labelsBlank = computed(() => lines.value.every(line => !line.text.some(Boolean)))

const canDismiss = ref(true)

const okButtonEnabled = computed(() => {
  return !props.config?.okButtonEnabled
    ? true
    : props.config.okButtonEnabled(+amount.value, inputValues.value)
})

const max = computed(() => {
  if(selectedInputIndex.value === undefined) {
    if(props.config?.max !== undefined) return props.config.max
  } else {
    const input = inputs.value?.[selectedInputIndex.value]
    if(input?.max !== undefined) return input.max
  }

  if(props.config?.maxSum === undefined) return undefined

  let values = [ ...inputValues.value ]

  if(selectedInputIndex.value !== undefined) {
    values.splice(selectedInputIndex.value, 1)
    values.unshift(+(amount.value || 0))
  }

  const total = values.filter(val => typeof val === 'number' && !isNaN(val))
    .reduce((acc, cur) => acc.add(Money.parse(cur as number)), Money.parse(0))

  return Money.parse(props.config.maxSum).subtract(total).toUnit()
})

// Any close not resolved by OK (backdrop, ESC, or the host flipping the
// model) cancels exactly once.
watch(numpadAmount, () => {
  if(selectedInputIndex.value === undefined) {
    amount.value = numpadAmount.value
    mutateInputs('auto')
    return
  }

  const input = inputs.value?.[selectedInputIndex.value]
  if (input?.type === 'number') {
    input.value = +numpadAmount.value
    input.strValue = numpadAmount.value
    highlightedInputIndex.value = undefined
  }

  mutateAmount('auto')
  mutateInputs('auto', { exclude: selectedInputIndex.value })
})

watch(highlightedInputIndex, (val) => {
  resetPending.value = val !== undefined
})

function onModalPresent() {
  resolved.value = false
  amount.value = props.config?.initialValue ? props.config?.initialValue + '' : '0'
  numpadAmount.value = props.config?.initialValue ? props.config?.initialValue + '' : '0'
  canDismiss.value = props?.config?.canDismiss === undefined || props?.config?.canDismiss

  inputs.value = props.config?.inputs?.map(i => {
    return {
      ...i,
      strValue: i.type === 'number' && i.value !== 0 ? Number(i.value).toFixed(props.config?.precision || 2) : ('' + i.value)
    }
  })
  selectedInputIndex.value = undefined
}

const selectInputField = (index?: number, ev?: MouseEvent) => {
  if(ev) {
    ev.preventDefault()
    ev.stopPropagation()
  }

  if(index === undefined) {
    mutateAmount('focus')
    selectedInputIndex.value = undefined
    highlightedInputIndex.value = undefined
    numpadAmount.value = amount.value
    return
  }

  const input = inputs.value?.[index]
  if(!input || input.type !== 'number') return

  const oldInput = selectedInputIndex.value !== undefined ? inputs.value?.[selectedInputIndex.value] : undefined

  if(oldInput?.type === 'number' && oldInput.strValue !== '0') {
    oldInput.strValue = getStrValue(oldInput)
  }

  mutateInputs('focus', { apply: index })
  selectedInputIndex.value = index
  numpadAmount.value = input.strValue

  setTimeout(() => (highlightedInputIndex.value = index), 100)
}

const mutateAmount = (mutateOn: 'focus' | 'auto') => {
  if(!inputs.value || props.config?.mutateOn !== mutateOn) return

  if(props.config?.mutator) {
    amount.value = props.config?.mutator(+amount.value, inputValues.value) + ''
  }
}

const mutateInputs = (mutateOn: 'focus' | 'auto', mutateIndex?: { apply?: number, exclude?: number }) => {
  if(!inputs.value) return

  inputs.value.forEach((input, index) => {
    if(mutateIndex?.exclude !== undefined && index === mutateIndex.exclude) return
    if(mutateIndex?.apply !== undefined && index !== mutateIndex.apply) return

    if(input.mutator && input?.mutateOn === mutateOn) {
      input.value = input.mutator(+amount.value, inputValues.value)
      input.strValue = getStrValue(input)
    }
  })
}

const confirm = () => {
  // Already settled: the keypad still takes keys while the sheet animates
  // out, and Enter right after Esc must not confirm what was just cancelled.
  if(resolved.value) return

  props.config?.confirmHandler?.({
    amount: +amount.value,
    inputs: inputs.value || []
  })

  resolved.value = true
  emit('update:modelValue', false)
}

const getStrValue = (input: ExtendedNumpadInputType): string => {
  if(input.type !== 'number') {
    return '' + input.value
  }

  if(input.value === 0) return '0'
  else return Number(input.value).toFixed(props.config?.precision || 2)
}

// immediate: lazily mounted with the model already open (see MainLayout).
watch(() => props.modelValue, (val) => {
  if(val) {
    onModalPresent()
    return
  }
  if(!resolved.value) {
    resolved.value = true
    props.config?.cancelHandler?.()
  }
}, { immediate: true })
</script>
